/**
 * Tempo relativo dos cards de atendimento.
 *
 * Duas apresentações, porque os mockups pedem as duas: `short` é o "4m"/"3h" do
 * rodapé dos cards Normal e Compacto; `long` é o "há 1 h" do bloco Conversa do
 * card de Detalhes.
 */

/** Minutos decorridos desde `iso`. `null` quando ausente ou não parseável. */
export const minutesSince = (iso: string | undefined, now: Date = new Date()): number | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  // Relógio adiantado do cliente não pode produzir tempo negativo.
  return Math.max(0, Math.floor((now.getTime() - then) / 60000));
};

export type RelativeTimeStyle = 'short' | 'long';

/**
 * Data ausente/inválida devolve o travessão do DS — estado vazio, não erro.
 * O `formatTime` anterior devolvia a string "Data inválida" dentro do card, que
 * é mensagem de defeito onde deveria haver ausência.
 */
export const formatRelativeTime = (
  iso: string | undefined,
  style: RelativeTimeStyle = 'short',
  now: Date = new Date(),
): string => {
  const minutes = minutesSince(iso, now);
  if (minutes === null) return '—';
  if (minutes < 1) return 'agora';
  if (minutes < 60) return style === 'long' ? `há ${minutes} min` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return style === 'long' ? `há ${hours} h` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return style === 'long' ? `há ${days} d` : `${days}d`;
};

/**
 * Tempo que a conversa passou EM FILA antes de alguém assumir:
 * `startedAt − createdAt`. Sem `startedAt`, a conversa ainda está na fila e a
 * conta corre até agora — daí as duas redações.
 *
 * O `startedAt` precisa ser o CRU do board (Tarefa 12): no `ConversationResponse`
 * ele coalesce com `createdAt` e toda conversa da fila diria "menos de 1 min".
 */
export const formatQueueWait = (
  createdAt: string | undefined,
  startedAt: string | undefined,
  now: Date = new Date(),
): string | undefined => {
  if (!createdAt) return undefined;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return undefined;
  const end = startedAt ? new Date(startedAt).getTime() : now.getTime();
  if (Number.isNaN(end)) return undefined;

  const minutes = Math.max(0, Math.floor((end - created) / 60000));
  const label =
    minutes < 1 ? 'menos de 1 min'
    : minutes < 60 ? `${minutes} min`
    : minutes < 24 * 60 ? `${Math.floor(minutes / 60)} h`
    : `${Math.floor(minutes / (24 * 60))} d`;

  return startedAt ? `Atendida após ${label} na fila` : `Na fila há ${label}`;
};
